import { Link, createFileRoute } from '@tanstack/react-router'
import { useTranslation } from 'react-i18next'
import { Download, Upload } from 'lucide-react'
import { useState } from 'react'
import { ExportLibraryModal, ImportLibraryModal, LibraryPanel } from '../../features/library'

export const Route = createFileRoute('/library/')({
  component: LibraryPage,
})

/**
 * The creature library management page at `/library`.
 *
 * Provides the main CRUD surface for creatures and categories, plus
 * export/import controls for backup and sharing. The page-level export/import
 * modals operate on the entire library at once, while individual creature
 * and category edit/delete actions are available inline via the `LibraryPanel`.
 */
function LibraryPage() {
  const { t } = useTranslation('library')
  const { t: tCommon } = useTranslation('common')
  const [isExportOpen, setIsExportOpen] = useState(false)
  const [isImportOpen, setIsImportOpen] = useState(false)

  return (
    <div className="space-y-6">
      <title>{t('libraryPageTitle')}</title>
      <meta name="description" content="Manage your creature library. Create, edit, and organise creatures by category, then add them to combat." />
      <div className="flex flex-wrap items-center justify-between gap-4 rounded-2xl border border-slate-200 bg-white px-6 py-4 shadow-sm">
        <div>
          <h2 className="text-xl font-semibold text-slate-900">{tCommon('creatureLibraryTitle')}</h2>
          <p className="text-sm text-slate-500">
            {t('libraryPageDescription')}
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            onClick={() => setIsExportOpen(true)}
            className="rounded-full border border-slate-300 px-4 py-2 text-sm font-semibold text-slate-700 transition hover:border-slate-400 inline-flex items-center gap-2"
          >
            <Download size={16} />
            {t('exportLibrary')}
          </button>
          <button
            type="button"
            onClick={() => setIsImportOpen(true)}
            className="rounded-full border border-slate-300 px-4 py-2 text-sm font-semibold text-slate-700 transition hover:border-slate-400 inline-flex items-center gap-2"
          >
            <Upload size={16} />
            {t('importLibrary')}
          </button>
          <Link
            to="/library/creature/new"
            className="rounded-full bg-slate-900 px-4 py-2 text-sm font-semibold text-white transition hover:bg-slate-800"
          >
            {t('newCreature')}
          </Link>
          <Link
            to="/library/category/new"
            className="rounded-full border border-slate-300 px-4 py-2 text-sm font-semibold text-slate-700 transition hover:border-slate-400"
          >
            {t('newCategory')}
          </Link>
        </div>
      </div>

      <LibraryPanel />

      <ExportLibraryModal
        isOpen={isExportOpen}
        onClose={() => setIsExportOpen(false)}
      />
      <ImportLibraryModal
        isOpen={isImportOpen}
        onClose={() => setIsImportOpen(false)}
      />
    </div>
  )
}
